import axios, { AxiosError } from "axios";
import { useCallback, useContext } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { TRADES_API_URL } from "../../../utils/env";
import { FilledTradesEndpointEnum } from "../../../enums/endpoints";
import { ToastEnum, ToastStatusEnum, useToast } from "../../toast";

export interface IStoreBuybackAndBurnRecord {
  Month: string;
  TotalVolume: number;
  Tier: string;
  BurnAmount: number;
  BuybackAmount: number;
  _CreatedTimestamp: number;
}

interface IStoreBuybackAndBurnsResponse {
  success: boolean;
  message: string;
  data?: {
    recordsInserted: number;
  };
}

export const useStoreBuybackAndBurns = () => {
  const { tokenId } = useContext(AuthContext);
  const { addToast } = useToast();

  const storeBuybackAndBurns = useCallback(
    async (records: IStoreBuybackAndBurnRecord[]): Promise<boolean> => {
      if (!tokenId) {
        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: "Authentication Required",
            status: ToastStatusEnum.ERROR,
          },
          4000
        );
        return false;
      }

      if (!records || records.length === 0) {
        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: "No Data to Store",
            subheader: "Please provide at least one record",
            status: ToastStatusEnum.ERROR,
          },
          4000
        );
        return false;
      }

      try {
        const response = await axios.post<IStoreBuybackAndBurnsResponse>(
          TRADES_API_URL(FilledTradesEndpointEnum.STORE_BUYBACK_AND_BURNS),
          { records },
          {
            withCredentials: true,
            headers: {
              Authorization: `Bearer ${tokenId}`,
              "Access-Control-Allow-Origin": "*",
            },
          }
        );

        if (response.data.success) {
          addToast(
            {
              type: ToastEnum.SIMPLE,
              header: "Buyback & Burn Record Stored",
              subheader: `Successfully stored ${response.data.data?.recordsInserted || records.length} record(s)`,
              status: ToastStatusEnum.SUCCESS,
            },
            4000
          );
          return true;
        }
        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: "Failed to Store Record",
            subheader: response.data.message || "Unknown error",
            status: ToastStatusEnum.ERROR,
          },
          4000
        );
        return false;
      } catch (error) {
        const errorMessage =
          (error as AxiosError<{ message?: string }>)?.response?.data?.message ||
          "Failed to store buyback and burn record";
        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: "Failed to Store Record",
            subheader: errorMessage,
            status: ToastStatusEnum.ERROR,
          },
          4000
        );
        return false;
      }
    },
    [tokenId, addToast]
  );

  return { storeBuybackAndBurns };
};
